import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Tooltip,
} from "@mui/material";
import {
  Campaign,
  Team as TeamType,
  Response,
  User,
  CampaignState,
} from "../../types";
import { CampaignTeam } from "./Team";
import { useMutation } from "@apollo/client";
import { SYNC_CAMPAIGN_TEAMS } from "../../Queries";
import { useErrorBoundary } from "../../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";

export const Teams = ({
  teams,
  campaign,
  users,
  responses,
  onExclude,
}: {
  teams: TeamType[];
  campaign: Campaign;
  users: User[];
  responses: Response[];
  onExclude: (campaign: Campaign, team: TeamType) => void;
}) => {
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // State
  //
  const [open, setOpen] = React.useState(false);

  //
  // Mutations
  //
  const [syncCampaignTeams, { loading: syncLoading }] = useMutation(
    SYNC_CAMPAIGN_TEAMS,
    {
      refetchQueries: ["GetGroups", "GetCampaign"],
      onError: (error) => setError(t("errors.courses.sync"), error),
      onCompleted: (data) => {
        console.log({ data });
      },
    },
  );

  //
  // Event Handlers
  //

  function handleOpenUpdateModal() {
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  };

  function handleSync() {
    const updateTeamsEnabled =
      campaign.course?.fullSyncEnabled && campaign.course?.fullSyncReady;
    if (!updateTeamsEnabled) {
      setError(
        "Your LMS isn't configured for full API access; please contact an administrator to enable full sync",
        null,
      );
    }
    syncCampaignTeams({ variables: { id: campaign.id, reimport: true } });
    setOpen(false);
  }

  //
  // Helpers
  //

  function getTeamResponses(team: TeamType) {
    return responses.filter(
      // (response) => team.userIds.indexOf(response.authorId) >= 0
      (response) => response.groupId === team.id,
    );
  }

  function getTeamAssignmentSubmissions(team: TeamType) {
    if (!campaign.assignment?.submissions) {
      return [];
    }
    return campaign.assignment.submissions.filter(
      (submission) => submission.groupId === team.id,
    );
  }

  const sortedTeams = [...teams].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );

  const isDraftOrScheduled =
    campaign.state === CampaignState.Draft ||
    campaign.state === CampaignState.Scheduled;
  const updateTeamsEnabled =
    campaign.course?.fullSyncEnabled &&
    campaign.course?.fullSyncReady &&
    isDraftOrScheduled;

  return (
    <>
      <Box sx={{ my: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Teams
          </Typography>
          {updateTeamsEnabled && (
            <Tooltip title={t("surveys.syncTeams.tooltip")}>
              <LoadingButton
                variant="outlined"
                color="light"
                size="small"
                onClick={handleOpenUpdateModal}
                loading={syncLoading}
              >
                {t("surveys.syncTeams.button")}
              </LoadingButton>
            </Tooltip>
          )}
        </Box>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {sortedTeams.map((team) => (
                <Grid item key={team.id} xs={12} md={6}>
                  <CampaignTeam
                    key={team.id}
                    campaign={campaign}
                    team={team}
                    users={users}
                    responses={getTeamResponses(team)}
                    assignmentSubmissions={getTeamAssignmentSubmissions(team)}
                    onExclude={() => onExclude(campaign, team)}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("surveys.syncTeams.dialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("surveys.syncTeams.dialogContent")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {t("surveys.syncTeams.dialogCancel")}
          </Button>
          <Button onClick={handleSync} autoFocus>
            {t("surveys.syncTeams.dialogRefresh")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
